import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { Buildings, ArrowRight, CheckCircle, Clock, Handshake, ShieldCheck, ChartLineUp } from "@phosphor-icons/react";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { autoPrefixUS } from "../../components/PhoneInput";

const FACILITY_TYPES = [
  "dialysis", "hospital", "skilled nursing", "clinic", "school", "home health", "MCO / insurance", "other",
];

const ROLES = [
  { value: "facility", label: "Facility — I need trips for patients/clients" },
  { value: "broker", label: "Broker — I refer or manage trip volume" },
  { value: "dispatcher", label: "Dispatcher — I want to join operations" },
];

export default function PartnerSignup() {
  const [searchParams] = useSearchParams();
  const initialRole = ROLES.some((r) => r.value === searchParams.get("role")) ? searchParams.get("role") : "facility";
  const [form, setForm] = useState({
    role: initialRole,
    organization: "", contact_name: "", email: "", phone: "",
    facility_type: "dialysis", monthly_trip_volume: "", message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (payload.role !== "facility") delete payload.facility_type;
      await apiClient.post("/partners/leads", payload);
      setSubmitted(true);
      toast.success("Thanks — we'll reach out within one business day.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit");
    } finally { setLoading(false); }
  };

  return (
    <main className="min-h-screen bg-bone" data-testid="partner-signup-page">
      <section className="relative overflow-hidden bg-brandblue-ink text-white">
        <div aria-hidden className="orb h-[560px] w-[560px] bg-brandblue/30 -top-40 -left-40" />
        <div aria-hidden className="orb h-[420px] w-[420px] bg-ova/25 -bottom-20 -right-10" />
        <div aria-hidden className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative max-w-6xl mx-auto px-6 pt-24 pb-16">
          <Link to="/" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-brandblue mb-6">
            <span>MedexOVA</span> <span className="text-white/30">/</span> <span className="text-white/50">For partners</span>
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="font-heading text-4xl sm:text-6xl font-black leading-[1.05] max-w-4xl"
          >
            Medical transport your facility can <span className="text-brandblue">stop worrying about</span>.
          </motion.h1>
          <p className="mt-5 max-w-2xl text-white/70 text-base">
            MedexOVA partners with dialysis centers, hospitals, SNFs, clinics, and IDD schools nationwide — powered by the OVA Dispatch network and our in-house concierge AI.
            As a licensed NEMT facilitator and transportation broker, we quote your volume, onboard local carriers in your market, and plug directly into your scheduling workflow.
            Brokers and dispatch teams looking to work with us can apply below too.
          </p>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl">
            {[
              { icon: Clock, k: "Avg dispatch", v: "< 6 min", sub: "request → driver assigned" },
              { icon: CheckCircle, k: "On-time rate", v: "98.4%", sub: "within ± 10 min window" },
              { icon: ShieldCheck, k: "HIPAA-aware", v: "E2E", sub: "BAAs available on request" },
              { icon: ChartLineUp, k: "Service footprint", v: "Nationwide", sub: "HQ in TX · expanding rapidly" },
            ].map((s) => (
              <div key={s.k} className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                <s.icon size={18} className="text-brandblue" />
                <div className="mt-2 text-[10px] uppercase tracking-[0.2em] font-bold text-white/50">{s.k}</div>
                <div className="font-heading text-xl font-black mt-1">{s.v}</div>
                <div className="text-xs text-white/50 mt-0.5">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-12">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-ova">Why facilities switch</div>
          <h2 className="font-heading text-3xl font-black text-ink mt-3">One vendor, the whole transport stack.</h2>
          <ul className="mt-6 space-y-5 text-slate-700">
            {[
              ["Ambulatory, wheelchair, stretcher — all in one API", "Drivers are background-checked and trained in patient handoff"],
              ["Recurring schedules for dialysis, chemo, school, wound care", "Skip holiday lists, pause/resume on demand"],
              ["Airport pickups with flight-number auto-adjust", "Landing + 30 min buffer, meet-and-greet optional"],
              ["Live driver map and rider SMS tracking link", "Stops the 'where's my ride?' calls cold"],
              ["Broker-partner model with OVA Dispatch", "You get national scale without swapping vendors"],
            ].map(([t, sub]) => (
              <li key={t} className="flex gap-3">
                <CheckCircle size={18} weight="fill" className="mt-1 text-brandblue shrink-0" />
                <div>
                  <div className="font-semibold text-ink">{t}</div>
                  <div className="text-sm text-slate-500">{sub}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
          data-testid="partner-form"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brandblue inline-flex items-center gap-2">
            <Handshake size={14} /> Partner inquiry
          </div>
          <h3 className="font-heading text-2xl font-black text-ink mt-2">Tell us a little about your facility</h3>
          {submitted ? (
            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900">
              <div className="font-heading text-lg font-bold">Got it — thank you.</div>
              <p className="mt-1">A member of our partnerships team will reach out within one business day. In the meantime you can try the <a href="/book" className="underline">booking form</a> or ask <b>Ava</b> anything on the homepage.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                <div className="sm:col-span-2">
                  <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-600">I am a…</label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    data-testid="partner-role"
                    className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                  >
                    {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <Field label="Organization" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} required testid="partner-org" />
                <Field label="Your name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} required testid="partner-name" />
                <Field label="Work email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="partner-email" />
                <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })}
                       onBlur={() => setForm((f) => ({ ...f, phone: autoPrefixUS(f.phone) }))}
                       required testid="partner-phone" />
                {form.role === "facility" && (
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-600">Facility type</label>
                    <select
                      value={form.facility_type}
                      onChange={(e) => setForm({ ...form, facility_type: e.target.value })}
                      data-testid="partner-type"
                      className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                    >
                      {FACILITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                )}
                <Field label="Monthly trip volume" value={form.monthly_trip_volume} onChange={(v) => setForm({ ...form, monthly_trip_volume: v })} placeholder="e.g. 120" testid="partner-volume" />
              </div>
              {form.role === "broker" && (
                <div className="mt-4 rounded-xl border border-brandblue/30 bg-brandblue/5 p-3 text-xs text-slate-700" data-testid="partner-broker-hint">
                  Already running your own fleet with drivers and vehicles?{" "}
                  <Link to="/partners/apply" className="text-brandblue font-semibold underline">Skip ahead to the full Transportation Provider application →</Link>
                </div>
              )}
              <div className="mt-4">
                <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-600">Tell us anything helpful</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4}
                  data-testid="partner-message"
                  className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  placeholder="Current pain points, trip types, coverage area…"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="partner-submit"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brandblue hover:bg-brandblue-hover text-white font-semibold px-5 py-3 rounded-md text-sm disabled:opacity-60"
              >
                {loading ? "Sending…" : <>Request a partnership call <ArrowRight size={14} weight="bold" /></>}
              </button>
              <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                We'll respond within one business day. For urgent needs call us at <a href="tel:+19409230878" className="text-brandblue font-semibold">(940) 923-0878</a>.
              </p>
            </>
          )}
        </motion.form>
      </section>
    </main>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder, onBlur }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
      />
    </div>
  );
}
